import React, { memo, lazy, Suspense } from "react";
import { Routes, Route, useNavigate } from "react-router-dom";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import { useSelector } from "react-redux";
import { Security, LoginCallback } from "@okta/okta-react";
import { toRelativeUrl } from "@okta/okta-auth-js";
import ScrollToTop from "../layouts/ScrollToTop";
import { CommonLoaderBackdropSx } from "../assets/styles/commonSx";
import Dashboard from "../pages/Dashboard";
import CreateProject from "../pages/Dashboard/CreateProject";
import Details from "../pages/Dashboard/Details";
import DiscountAndPlanogram from "../pages/DiscountAndPlanogram";
import ManageStore from "../pages/ManageStore";
import NotFound from "../pages/NotFound";
import Reports from "../pages/Reports";
import Departments from "../pages/ManagePlanograms/Departments";
import Pogs from "../pages/ManagePlanograms/Pogs";
import Items from "../pages/ManagePlanograms/Items";
import StoreSuppliesPogs from "../pages/StoreSupplies/StoreSuppliesPogs";
import StoreSuppliesItems from "../pages/StoreSupplies/StoreSuppliesItems";
import ProvideStore from "../layouts/ProvideStore";
import PermissionsGate from "../permissions/PermissionsGate";
import { SCOPES } from "../permissions/permissions-map";
import { RequiredAuth } from "./SecureRoute";
import oktaAuth from "../okta";
import AuthLoader from "./AuthLoader";

const SuspenseLoader = memo(() => (
  <Backdrop sx={CommonLoaderBackdropSx} open>
    <CircularProgress color="inherit" />
  </Backdrop>
));

// Lazy Routes
const LazyManageNotes = lazy(() => import("../pages/ManageNotes"));

const AppRoutes = () => {
  const navigate = useNavigate();

  const { isStoreSupplies } = useSelector((store) => store.global.store);

  const restoreOriginalUri = (_oktaAuth, originalUri) => {
    navigate(toRelativeUrl(originalUri || "/", window.location.origin));
  };

  return (
    <ScrollToTop>
      <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
        <Routes>
          <Route
            path={`login/callback`}
            element={
              <LoginCallback
                loadingElement={<AuthLoader>Redirecting...</AuthLoader>}
              />
            }
          />
          <Route element={<RequiredAuth />}>
            <Route path="/" element={<Dashboard />} />
            <Route
              path="create-project"
              element={
                <PermissionsGate
                  scopes={[SCOPES.canCreate]}
                  isAutoRedirect={true}
                >
                  <CreateProject />
                </PermissionsGate>
              }
            />
            <Route element={<ProvideStore />}>
              <Route path="project-details" element={<Details />} />
              <Route path="discounting" element={<DiscountAndPlanogram />} />
              <Route path="manage-pog" element={<DiscountAndPlanogram />} />

              {!isStoreSupplies && (
                <Route path="manage-planograms">
                  <Route path="departments" element={<Departments />} />
                  <Route path="pogs/:departmentLetter" element={<Pogs />} />
                  <Route
                    path="items/:departmentLetter/:pogId"
                    element={<Items />}
                  />
                </Route>
              )}
              <Route path="store-supplies">
                <Route path="pogs" element={<StoreSuppliesPogs />} />
                <Route
                  path="items/:departmentLetter/:pogId"
                  element={<StoreSuppliesItems />}
                />
              </Route>
              <Route path="manage-planograms-v1" element={<ManageStore />} />
              <Route path="manage-orders" element={<ManageStore />} />
            </Route>
            <Route path="project-reports" element={<Reports />} />
            <Route path="admin">
              <Route
                path="manage-notes"
                element={
                  <PermissionsGate
                    scopes={[SCOPES.canAll]}
                    isAutoRedirect={true}
                  >
                    <Suspense fallback={<SuspenseLoader />}>
                      <LazyManageNotes />
                    </Suspense>
                  </PermissionsGate>
                }
              />
            </Route>
            <Route path="error" element={<NotFound type="error" />} />
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </Security>
    </ScrollToTop>
  );
};

export default AppRoutes;
